import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/components/common";
import { jobsService } from "@/api/services/jobs.service";
import { formsService } from "@/api/services/forms.service";
import { jobToUpdateDto } from "@/utils/jobUtils";
import { getAdditionalInformationFromNotes } from "@/utils/buildingPermitNotes";
import { nowAUSISO } from "@/utils/dateTime";
import { FormFieldsEditor } from "@/features/admin/forms/components/FormFieldsEditor";
import styles from "./BuildingPermitModal.module.css";
const DRAFT_NOTES_PREFIX = "Building Permit Application (Draft): ";
const DEFAULT_PROPERTY_DETAILS = {
    buildingName: "",
    shopNo: "",
    fullAddress: "",
    lotNumber: "",
    lpPs: "",
    vol: "",
    fol: "",
    crownAllot: "",
    section: "",
    parish: "",
    county: "",
    council: "",
    landOwnedByCrownOrPublicAuthority: false,
};
const DEFAULT_BUILDER_NATURAL_PERSON = {
    contactPerson: "",
    email: "",
    address: "",
    phone: "",
};
const DEFAULT_BUILDING_PRACTITIONER = {
    name: "",
    categoryOrClass: "",
    registrationNo: "",
};
const DEFAULT_OWNER_BUILDER_SECTION = {
    intendsOwnerBuilder: false,
    ownerBuilderCertificateOfConsentNo: "",
    signedByOwner: "",
};
const DEFAULT_NATURE_OF_BUILDING_WORK = {
    constructionNewBuilding: false,
    demolitionBuilding: false,
    reErectionBuilding: false,
    alterationsExistingBuilding: false,
    removalBuilding: false,
    extensionBuilding: false,
    tenancyFitout: false,
    changeOfUse: false,
    otherDescriptionEnabled: false,
    otherDescription: "",
    constructionSwimmingPoolOrSpa: false,
    constructionSwimmingPoolOrSpaBarrier: false,
    descriptionOfProposedBuildingWorks: "",
    descriptionOfUseOfBuilding: "",
};
const DEFAULT_BUILDING_DETAILS = {
    proposedUseOfBuilding: "",
    proposedClassOfBuilding: "",
    currentUseOfBuilding: "",
    currentClassOfBuilding: "",
    includesSocialHousing: false,
    isEmergencyRecoveryDwelling: false,
};
const DEFAULT_COST_OF_BUILDING_WORK = {
    nonOwnerBuilder: {
        hasLumpSumContract: false,
        lumpSumContractPrice: "",
        hasCostPlusContract: false,
        costPlusEstimatedTotal: "",
        chattelsCost: "",
        prescribedExcludedItemsCost: "",
        relatesToMixedClasses: false,
        relatesToMixedClassesNotes: "",
        isStageOfWholeWork: false,
        stageExtentDescription: "",
        hasWholeLumpSumContract: false,
        wholeLumpSumContractPrice: "",
        hasWholeCostPlusContract: false,
        wholeCostPlusEstimatedTotal: "",
        wholeChattelsCost: "",
        wholePrescribedExcludedItemsCost: "",
    },
    ownerBuilder: {
        isOwnerBuilder: false,
        ownerBuilderCertificateOfConsentNo: "",
        ownerBuilderEstimatedCost: "",
        ownerEngagesBuilders: false,
        engagedBuilders: [],
        ownerChattelsCost: "",
        ownerPrescribedExcludedItemsCost: "",
        ownerIsStageOfWholeWork: false,
        ownerStageExtentDescription: "",
        ownerWholeEstimatedCost: "",
        ownerWholeChattelsCost: "",
        ownerWholePrescribedExcludedItemsCost: "",
        ownerHasLumpSumBuilderForStages: false,
        ownerLumpSumStagesPrice: "",
        ownerHasCostPlusBuilderForStages: false,
        ownerCostPlusStagesEstimate: "",
        ownerStagesChattelsCost: "",
        ownerStagesExcludedItemsCost: "",
        ownerRelatesToMixedClasses: false,
        ownerRelatesToMixedClassesNotes: "",
    },
};
const DEFAULT_SIGNATURE_DECLARATION = {
    applicantSignature: "",
    printName: "",
    signatureDate: "",
};
const DEFAULT_FORM_DATA = {
    applicantRole: "",
    applicantAcnArbn: "",
    contactName: "",
    email: "",
    companyName: "",
    phone: "",
    address: "",
    addressForServing: "",
    isLiaseeOrLicensee: false,
    propertyDetails: { ...DEFAULT_PROPERTY_DETAILS },
    ownerName: "",
    ownerContactPerson: "",
    ownerEmail: "",
    ownerCompanyName: "",
    ownerPhone: "",
    ownerAddress: "",
    acnArbn: "",
    lesseeResponsible: false,
    tenantContactPerson: "",
    tenantEmail: "",
    tenantCompanyName: "",
    tenantPhone: "",
    tenantAddress: "",
    builderName: "",
    builderContactPerson: "",
    builderEmail: "",
    builderCompanyName: "",
    builderPhone: "",
    builderAddress: "",
    builderPractitionerNo: "",
    builderAcnArbn: "",
    builderPostalAddress: "",
    builderNaturalPerson: { ...DEFAULT_BUILDER_NATURAL_PERSON },
    buildingPractitioners: [
        { ...DEFAULT_BUILDING_PRACTITIONER },
        { ...DEFAULT_BUILDING_PRACTITIONER },
        { ...DEFAULT_BUILDING_PRACTITIONER },
    ],
    ownerBuilderSection: { ...DEFAULT_OWNER_BUILDER_SECTION },
    natureOfBuildingWork: { ...DEFAULT_NATURE_OF_BUILDING_WORK },
    buildingDetails: { ...DEFAULT_BUILDING_DETAILS },
    costOfBuildingWork: { ...DEFAULT_COST_OF_BUILDING_WORK },
    signatureDeclaration: { ...DEFAULT_SIGNATURE_DECLARATION },
};
const DEFAULT_FORM_TITLE = "APPLICATION AND AGREEMENT FORM FOR A BUILDING PERMIT - COMMERCIAL";
const DEFAULT_REGULATION_TEXT = "APPLICATION FORM FOR A BUILDING PERMIT BUILDING ACT 1993, BUILDING REGULATIONS 2018 REGULATION 24 Form 1 (As from 16/01/2026)";
const buildRegulationText = (customSubtitleDate) => {
    if (!customSubtitleDate)
        return DEFAULT_REGULATION_TEXT;
    return `APPLICATION FORM FOR A BUILDING PERMIT BUILDING ACT 1993, BUILDING REGULATIONS 2018 REGULATION 24 Form 1 (As from ${customSubtitleDate})`;
};
const MODAL_FORM_DATA_SIMPLE_KEYS = [
    "applicantRole",
    "applicantAcnArbn",
    "contactName",
    "email",
    "companyName",
    "phone",
    "address",
    "addressForServing",
    "isLiaseeOrLicensee",
    "ownerName",
    "ownerContactPerson",
    "ownerEmail",
    "ownerCompanyName",
    "ownerPhone",
    "ownerAddress",
    "acnArbn",
    "lesseeResponsible",
    "tenantContactPerson",
    "tenantEmail",
    "tenantCompanyName",
    "tenantPhone",
    "tenantAddress",
    "builderName",
    "builderContactPerson",
    "builderEmail",
    "builderCompanyName",
    "builderPhone",
    "builderAddress",
    "builderPractitionerNo",
    "builderAcnArbn",
    "builderPostalAddress",
];
function parseBuildingPermitDraftFromNotes(notes) {
    if (!notes || !notes.includes(DRAFT_NOTES_PREFIX))
        return null;
    const jsonStart = notes.indexOf(DRAFT_NOTES_PREFIX) + DRAFT_NOTES_PREFIX.length;
    const jsonStr = notes.slice(jsonStart);
    try {
        const parsed = JSON.parse(jsonStr);
        const merged = {
            ...DEFAULT_FORM_DATA,
            propertyDetails: { ...DEFAULT_PROPERTY_DETAILS },
            builderNaturalPerson: { ...DEFAULT_BUILDER_NATURAL_PERSON },
            buildingPractitioners: [
                { ...DEFAULT_BUILDING_PRACTITIONER },
                { ...DEFAULT_BUILDING_PRACTITIONER },
                { ...DEFAULT_BUILDING_PRACTITIONER },
            ],
            ownerBuilderSection: { ...DEFAULT_OWNER_BUILDER_SECTION },
            natureOfBuildingWork: { ...DEFAULT_NATURE_OF_BUILDING_WORK },
            buildingDetails: { ...DEFAULT_BUILDING_DETAILS },
            costOfBuildingWork: { ...DEFAULT_COST_OF_BUILDING_WORK },
            signatureDeclaration: { ...DEFAULT_SIGNATURE_DECLARATION },
        };
        for (const key of MODAL_FORM_DATA_SIMPLE_KEYS) {
            if (key in parsed) {
                const v = parsed[key];
                if (typeof v === "string" || typeof v === "boolean") {
                    merged[key] = v;
                }
            }
        }
        if (parsed.propertyDetails && typeof parsed.propertyDetails === "object") {
            const pd = parsed.propertyDetails;
            merged.propertyDetails = {
                buildingName: typeof pd.buildingName === "string" ? pd.buildingName : "",
                shopNo: typeof pd.shopNo === "string" ? pd.shopNo : "",
                fullAddress: typeof pd.fullAddress === "string" ? pd.fullAddress : "",
                lotNumber: typeof pd.lotNumber === "string" ? pd.lotNumber : "",
                lpPs: typeof pd.lpPs === "string" ? pd.lpPs : "",
                vol: typeof pd.vol === "string" ? pd.vol : "",
                fol: typeof pd.fol === "string" ? pd.fol : "",
                crownAllot: typeof pd.crownAllot === "string" ? pd.crownAllot : "",
                section: typeof pd.section === "string" ? pd.section : "",
                parish: typeof pd.parish === "string" ? pd.parish : "",
                county: typeof pd.county === "string" ? pd.county : "",
                council: typeof pd.council === "string" ? pd.council : "",
                landOwnedByCrownOrPublicAuthority: typeof pd.landOwnedByCrownOrPublicAuthority === "boolean" ? pd.landOwnedByCrownOrPublicAuthority : false,
            };
        }
        if (parsed.builderNaturalPerson && typeof parsed.builderNaturalPerson === "object") {
            const bnp = parsed.builderNaturalPerson;
            merged.builderNaturalPerson = {
                contactPerson: typeof bnp.contactPerson === "string" ? bnp.contactPerson : "",
                email: typeof bnp.email === "string" ? bnp.email : "",
                address: typeof bnp.address === "string" ? bnp.address : "",
                phone: typeof bnp.phone === "string" ? bnp.phone : "",
            };
        }
        if (parsed.buildingPractitioners && Array.isArray(parsed.buildingPractitioners)) {
            merged.buildingPractitioners = parsed.buildingPractitioners.map((bp) => {
                const item = bp;
                return {
                    name: typeof item.name === "string" ? item.name : "",
                    categoryOrClass: typeof item.categoryOrClass === "string" ? item.categoryOrClass : "",
                    registrationNo: typeof item.registrationNo === "string" ? item.registrationNo : "",
                };
            });
        }
        if (parsed.ownerBuilderSection && typeof parsed.ownerBuilderSection === "object") {
            const obs = parsed.ownerBuilderSection;
            merged.ownerBuilderSection = {
                intendsOwnerBuilder: typeof obs.intendsOwnerBuilder === "boolean" ? obs.intendsOwnerBuilder : false,
                ownerBuilderCertificateOfConsentNo: typeof obs.ownerBuilderCertificateOfConsentNo === "string" ? obs.ownerBuilderCertificateOfConsentNo : "",
                signedByOwner: typeof obs.signedByOwner === "string" ? obs.signedByOwner : "",
            };
        }
        if (parsed.natureOfBuildingWork && typeof parsed.natureOfBuildingWork === "object") {
            const nobw = parsed.natureOfBuildingWork;
            merged.natureOfBuildingWork = {
                constructionNewBuilding: typeof nobw.constructionNewBuilding === "boolean" ? nobw.constructionNewBuilding : false,
                demolitionBuilding: typeof nobw.demolitionBuilding === "boolean" ? nobw.demolitionBuilding : false,
                reErectionBuilding: typeof nobw.reErectionBuilding === "boolean" ? nobw.reErectionBuilding : false,
                alterationsExistingBuilding: typeof nobw.alterationsExistingBuilding === "boolean" ? nobw.alterationsExistingBuilding : false,
                removalBuilding: typeof nobw.removalBuilding === "boolean" ? nobw.removalBuilding : false,
                extensionBuilding: typeof nobw.extensionBuilding === "boolean" ? nobw.extensionBuilding : false,
                tenancyFitout: typeof nobw.tenancyFitout === "boolean" ? nobw.tenancyFitout : false,
                changeOfUse: typeof nobw.changeOfUse === "boolean" ? nobw.changeOfUse : false,
                otherDescriptionEnabled: typeof nobw.otherDescriptionEnabled === "boolean" ? nobw.otherDescriptionEnabled : false,
                otherDescription: typeof nobw.otherDescription === "string" ? nobw.otherDescription : "",
                constructionSwimmingPoolOrSpa: typeof nobw.constructionSwimmingPoolOrSpa === "boolean" ? nobw.constructionSwimmingPoolOrSpa : false,
                constructionSwimmingPoolOrSpaBarrier: typeof nobw.constructionSwimmingPoolOrSpaBarrier === "boolean" ? nobw.constructionSwimmingPoolOrSpaBarrier : false,
                descriptionOfProposedBuildingWorks: typeof nobw.descriptionOfProposedBuildingWorks === "string" ? nobw.descriptionOfProposedBuildingWorks : "",
                descriptionOfUseOfBuilding: typeof nobw.descriptionOfUseOfBuilding === "string" ? nobw.descriptionOfUseOfBuilding : "",
            };
        }
        if (parsed.buildingDetails && typeof parsed.buildingDetails === "object") {
            const bd = parsed.buildingDetails;
            merged.buildingDetails = {
                proposedUseOfBuilding: typeof bd.proposedUseOfBuilding === "string" ? bd.proposedUseOfBuilding : "",
                proposedClassOfBuilding: typeof bd.proposedClassOfBuilding === "string" ? bd.proposedClassOfBuilding : "",
                currentUseOfBuilding: typeof bd.currentUseOfBuilding === "string" ? bd.currentUseOfBuilding : "",
                currentClassOfBuilding: typeof bd.currentClassOfBuilding === "string" ? bd.currentClassOfBuilding : "",
                includesSocialHousing: typeof bd.includesSocialHousing === "boolean" ? bd.includesSocialHousing : false,
                isEmergencyRecoveryDwelling: typeof bd.isEmergencyRecoveryDwelling === "boolean" ? bd.isEmergencyRecoveryDwelling : false,
            };
        }
        if (parsed.costOfBuildingWork && typeof parsed.costOfBuildingWork === "object") {
            const cobw = parsed.costOfBuildingWork;
            const nonOwner = cobw.nonOwnerBuilder;
            const owner = cobw.ownerBuilder;
            const engagedBuildersRaw = owner?.engagedBuilders;
            const engagedBuilders = Array.isArray(engagedBuildersRaw)
                ? engagedBuildersRaw.map((eb) => {
                    const item = eb;
                    return { amount: typeof item.amount === "string" ? item.amount : "" };
                })
                : [];
            merged.costOfBuildingWork = {
                nonOwnerBuilder: {
                    hasLumpSumContract: typeof nonOwner?.hasLumpSumContract === "boolean" ? nonOwner.hasLumpSumContract : false,
                    lumpSumContractPrice: typeof nonOwner?.lumpSumContractPrice === "string" ? nonOwner.lumpSumContractPrice : "",
                    hasCostPlusContract: typeof nonOwner?.hasCostPlusContract === "boolean" ? nonOwner.hasCostPlusContract : false,
                    costPlusEstimatedTotal: typeof nonOwner?.costPlusEstimatedTotal === "string" ? nonOwner.costPlusEstimatedTotal : "",
                    chattelsCost: typeof nonOwner?.chattelsCost === "string" ? nonOwner.chattelsCost : "",
                    prescribedExcludedItemsCost: typeof nonOwner?.prescribedExcludedItemsCost === "string" ? nonOwner.prescribedExcludedItemsCost : "",
                    relatesToMixedClasses: typeof nonOwner?.relatesToMixedClasses === "boolean" ? nonOwner.relatesToMixedClasses : false,
                    relatesToMixedClassesNotes: typeof nonOwner?.relatesToMixedClassesNotes === "string" ? nonOwner.relatesToMixedClassesNotes : "",
                    isStageOfWholeWork: typeof nonOwner?.isStageOfWholeWork === "boolean" ? nonOwner.isStageOfWholeWork : false,
                    stageExtentDescription: typeof nonOwner?.stageExtentDescription === "string" ? nonOwner.stageExtentDescription : "",
                    hasWholeLumpSumContract: typeof nonOwner?.hasWholeLumpSumContract === "boolean" ? nonOwner.hasWholeLumpSumContract : false,
                    wholeLumpSumContractPrice: typeof nonOwner?.wholeLumpSumContractPrice === "string" ? nonOwner.wholeLumpSumContractPrice : "",
                    hasWholeCostPlusContract: typeof nonOwner?.hasWholeCostPlusContract === "boolean" ? nonOwner.hasWholeCostPlusContract : false,
                    wholeCostPlusEstimatedTotal: typeof nonOwner?.wholeCostPlusEstimatedTotal === "string" ? nonOwner.wholeCostPlusEstimatedTotal : "",
                    wholeChattelsCost: typeof nonOwner?.wholeChattelsCost === "string" ? nonOwner.wholeChattelsCost : "",
                    wholePrescribedExcludedItemsCost: typeof nonOwner?.wholePrescribedExcludedItemsCost === "string" ? nonOwner.wholePrescribedExcludedItemsCost : "",
                },
                ownerBuilder: {
                    isOwnerBuilder: typeof owner?.isOwnerBuilder === "boolean" ? owner.isOwnerBuilder : false,
                    ownerBuilderCertificateOfConsentNo: typeof owner?.ownerBuilderCertificateOfConsentNo === "string" ? owner.ownerBuilderCertificateOfConsentNo : "",
                    ownerBuilderEstimatedCost: typeof owner?.ownerBuilderEstimatedCost === "string" ? owner.ownerBuilderEstimatedCost : "",
                    ownerEngagesBuilders: typeof owner?.ownerEngagesBuilders === "boolean" ? owner.ownerEngagesBuilders : false,
                    engagedBuilders,
                    ownerChattelsCost: typeof owner?.ownerChattelsCost === "string" ? owner.ownerChattelsCost : "",
                    ownerPrescribedExcludedItemsCost: typeof owner?.ownerPrescribedExcludedItemsCost === "string" ? owner.ownerPrescribedExcludedItemsCost : "",
                    ownerIsStageOfWholeWork: typeof owner?.ownerIsStageOfWholeWork === "boolean" ? owner.ownerIsStageOfWholeWork : false,
                    ownerStageExtentDescription: typeof owner?.ownerStageExtentDescription === "string" ? owner.ownerStageExtentDescription : "",
                    ownerWholeEstimatedCost: typeof owner?.ownerWholeEstimatedCost === "string" ? owner.ownerWholeEstimatedCost : "",
                    ownerWholeChattelsCost: typeof owner?.ownerWholeChattelsCost === "string" ? owner.ownerWholeChattelsCost : "",
                    ownerWholePrescribedExcludedItemsCost: typeof owner?.ownerWholePrescribedExcludedItemsCost === "string" ? owner.ownerWholePrescribedExcludedItemsCost : "",
                    ownerHasLumpSumBuilderForStages: typeof owner?.ownerHasLumpSumBuilderForStages === "boolean" ? owner.ownerHasLumpSumBuilderForStages : false,
                    ownerLumpSumStagesPrice: typeof owner?.ownerLumpSumStagesPrice === "string" ? owner.ownerLumpSumStagesPrice : "",
                    ownerHasCostPlusBuilderForStages: typeof owner?.ownerHasCostPlusBuilderForStages === "boolean" ? owner.ownerHasCostPlusBuilderForStages : false,
                    ownerCostPlusStagesEstimate: typeof owner?.ownerCostPlusStagesEstimate === "string" ? owner.ownerCostPlusStagesEstimate : "",
                    ownerStagesChattelsCost: typeof owner?.ownerStagesChattelsCost === "string" ? owner.ownerStagesChattelsCost : "",
                    ownerStagesExcludedItemsCost: typeof owner?.ownerStagesExcludedItemsCost === "string" ? owner.ownerStagesExcludedItemsCost : "",
                    ownerRelatesToMixedClasses: typeof owner?.ownerRelatesToMixedClasses === "boolean" ? owner.ownerRelatesToMixedClasses : false,
                    ownerRelatesToMixedClassesNotes: typeof owner?.ownerRelatesToMixedClassesNotes === "string" ? owner.ownerRelatesToMixedClassesNotes : "",
                },
            };
        }
        if (parsed.signatureDeclaration && typeof parsed.signatureDeclaration === "object") {
            const sd = parsed.signatureDeclaration;
            merged.signatureDeclaration = {
                applicantSignature: typeof sd.applicantSignature === "string" ? sd.applicantSignature : "",
                printName: typeof sd.printName === "string" ? sd.printName : "",
                signatureDate: typeof sd.signatureDate === "string" ? sd.signatureDate : "",
            };
        }
        if (typeof parsed.additionalInformation === "string") {
            merged.additionalInformation = parsed.additionalInformation;
        }
        return merged;
    }
    catch {
        return null;
    }
}
function mapApiFormDataToModal(data) {
    if (!data) {
        return {
            ...DEFAULT_FORM_DATA,
            propertyDetails: { ...DEFAULT_PROPERTY_DETAILS },
            builderNaturalPerson: { ...DEFAULT_BUILDER_NATURAL_PERSON },
            buildingPractitioners: [
                { ...DEFAULT_BUILDING_PRACTITIONER },
                { ...DEFAULT_BUILDING_PRACTITIONER },
                { ...DEFAULT_BUILDING_PRACTITIONER },
            ],
            ownerBuilderSection: { ...DEFAULT_OWNER_BUILDER_SECTION },
            natureOfBuildingWork: { ...DEFAULT_NATURE_OF_BUILDING_WORK },
            buildingDetails: { ...DEFAULT_BUILDING_DETAILS },
            costOfBuildingWork: { ...DEFAULT_COST_OF_BUILDING_WORK },
            signatureDeclaration: { ...DEFAULT_SIGNATURE_DECLARATION },
        };
    }
    return {
        applicantRole: data.applicantRole ?? DEFAULT_FORM_DATA.applicantRole,
        applicantAcnArbn: data.applicantAcnArbn ?? DEFAULT_FORM_DATA.applicantAcnArbn,
        contactName: data.contactName ?? DEFAULT_FORM_DATA.contactName,
        email: data.email ?? DEFAULT_FORM_DATA.email,
        companyName: data.companyName ?? DEFAULT_FORM_DATA.companyName,
        phone: data.phone ?? DEFAULT_FORM_DATA.phone,
        address: data.address ?? DEFAULT_FORM_DATA.address,
        addressForServing: data.servingAddress ?? DEFAULT_FORM_DATA.addressForServing,
        isLiaseeOrLicensee: data.isLeaseeOrLicensee ?? DEFAULT_FORM_DATA.isLiaseeOrLicensee,
        propertyDetails: {
            buildingName: data.propertyDetails?.buildingName ?? DEFAULT_PROPERTY_DETAILS.buildingName,
            shopNo: data.propertyDetails?.shopNo ?? DEFAULT_PROPERTY_DETAILS.shopNo,
            fullAddress: data.propertyDetails?.fullAddress ?? DEFAULT_PROPERTY_DETAILS.fullAddress,
            lotNumber: data.propertyDetails?.lotNumber ?? DEFAULT_PROPERTY_DETAILS.lotNumber,
            lpPs: data.propertyDetails?.lpPs ?? DEFAULT_PROPERTY_DETAILS.lpPs,
            vol: data.propertyDetails?.vol ?? DEFAULT_PROPERTY_DETAILS.vol,
            fol: data.propertyDetails?.fol ?? DEFAULT_PROPERTY_DETAILS.fol,
            crownAllot: data.propertyDetails?.crownAllot ?? DEFAULT_PROPERTY_DETAILS.crownAllot,
            section: data.propertyDetails?.section ?? DEFAULT_PROPERTY_DETAILS.section,
            parish: data.propertyDetails?.parish ?? DEFAULT_PROPERTY_DETAILS.parish,
            county: data.propertyDetails?.county ?? DEFAULT_PROPERTY_DETAILS.county,
            council: data.propertyDetails?.council ?? DEFAULT_PROPERTY_DETAILS.council,
            landOwnedByCrownOrPublicAuthority: data.propertyDetails?.landOwnedByCrownOrPublicAuthority ?? DEFAULT_PROPERTY_DETAILS.landOwnedByCrownOrPublicAuthority,
        },
        ownerName: data.buildingOwner?.name ?? DEFAULT_FORM_DATA.ownerName,
        ownerContactPerson: data.buildingOwner?.contactPerson ?? DEFAULT_FORM_DATA.ownerContactPerson,
        ownerEmail: data.buildingOwner?.email ?? DEFAULT_FORM_DATA.ownerEmail,
        ownerCompanyName: data.buildingOwner?.company ?? DEFAULT_FORM_DATA.ownerCompanyName,
        ownerPhone: data.buildingOwner?.phone ?? DEFAULT_FORM_DATA.ownerPhone,
        ownerAddress: data.buildingOwner?.address ?? DEFAULT_FORM_DATA.ownerAddress,
        acnArbn: data.buildingOwner?.acnArbn ?? data.buildingOwner?.acn ?? DEFAULT_FORM_DATA.acnArbn,
        lesseeResponsible: data.lesseeResponsible ?? DEFAULT_FORM_DATA.lesseeResponsible,
        tenantContactPerson: data.tenant?.contactPerson ?? DEFAULT_FORM_DATA.tenantContactPerson,
        tenantEmail: data.tenant?.email ?? DEFAULT_FORM_DATA.tenantEmail,
        tenantCompanyName: data.tenant?.company ?? DEFAULT_FORM_DATA.tenantCompanyName,
        tenantPhone: data.tenant?.phone ?? DEFAULT_FORM_DATA.tenantPhone,
        tenantAddress: data.tenant?.address ?? DEFAULT_FORM_DATA.tenantAddress,
        builderName: data.builder?.name ?? DEFAULT_FORM_DATA.builderName,
        builderContactPerson: data.builder?.contactPerson ?? DEFAULT_FORM_DATA.builderContactPerson,
        builderEmail: data.builder?.email ?? DEFAULT_FORM_DATA.builderEmail,
        builderCompanyName: data.builder?.company ?? DEFAULT_FORM_DATA.builderCompanyName,
        builderPhone: data.builder?.phone ?? DEFAULT_FORM_DATA.builderPhone,
        builderAddress: data.builder?.address ?? DEFAULT_FORM_DATA.builderAddress,
        builderPractitionerNo: data.builder?.practitionerNo ?? DEFAULT_FORM_DATA.builderPractitionerNo,
        builderAcnArbn: data.builder?.acnArbn ?? DEFAULT_FORM_DATA.builderAcnArbn,
        builderPostalAddress: data.builder?.postalAddress ?? DEFAULT_FORM_DATA.builderPostalAddress,
        builderNaturalPerson: {
            contactPerson: data.builder?.naturalPerson?.contactPerson ?? DEFAULT_BUILDER_NATURAL_PERSON.contactPerson,
            email: data.builder?.naturalPerson?.email ?? DEFAULT_BUILDER_NATURAL_PERSON.email,
            address: data.builder?.naturalPerson?.address ?? DEFAULT_BUILDER_NATURAL_PERSON.address,
            phone: data.builder?.naturalPerson?.phone ?? DEFAULT_BUILDER_NATURAL_PERSON.phone,
        },
        buildingPractitioners: (data.buildingPractitioners ?? []).map((bp) => ({
            name: bp?.name ?? "",
            categoryOrClass: bp?.categoryOrClass ?? "",
            registrationNo: bp?.registrationNo ?? "",
        })),
        ownerBuilderSection: {
            intendsOwnerBuilder: data.ownerBuilderSection?.intendsOwnerBuilder ?? DEFAULT_OWNER_BUILDER_SECTION.intendsOwnerBuilder,
            ownerBuilderCertificateOfConsentNo: data.ownerBuilderSection?.ownerBuilderCertificateOfConsentNo ?? DEFAULT_OWNER_BUILDER_SECTION.ownerBuilderCertificateOfConsentNo,
            signedByOwner: data.ownerBuilderSection?.signedByOwner ?? DEFAULT_OWNER_BUILDER_SECTION.signedByOwner,
        },
        natureOfBuildingWork: {
            constructionNewBuilding: data.natureOfBuildingWork?.constructionNewBuilding ?? DEFAULT_NATURE_OF_BUILDING_WORK.constructionNewBuilding,
            demolitionBuilding: data.natureOfBuildingWork?.demolitionBuilding ?? DEFAULT_NATURE_OF_BUILDING_WORK.demolitionBuilding,
            reErectionBuilding: data.natureOfBuildingWork?.reErectionBuilding ?? DEFAULT_NATURE_OF_BUILDING_WORK.reErectionBuilding,
            alterationsExistingBuilding: data.natureOfBuildingWork?.alterationsExistingBuilding ?? DEFAULT_NATURE_OF_BUILDING_WORK.alterationsExistingBuilding,
            removalBuilding: data.natureOfBuildingWork?.removalBuilding ?? DEFAULT_NATURE_OF_BUILDING_WORK.removalBuilding,
            extensionBuilding: data.natureOfBuildingWork?.extensionBuilding ?? DEFAULT_NATURE_OF_BUILDING_WORK.extensionBuilding,
            tenancyFitout: data.natureOfBuildingWork?.tenancyFitout ?? DEFAULT_NATURE_OF_BUILDING_WORK.tenancyFitout,
            changeOfUse: data.natureOfBuildingWork?.changeOfUse ?? DEFAULT_NATURE_OF_BUILDING_WORK.changeOfUse,
            otherDescriptionEnabled: data.natureOfBuildingWork?.otherDescriptionEnabled ?? DEFAULT_NATURE_OF_BUILDING_WORK.otherDescriptionEnabled,
            otherDescription: data.natureOfBuildingWork?.otherDescription ?? DEFAULT_NATURE_OF_BUILDING_WORK.otherDescription,
            constructionSwimmingPoolOrSpa: data.natureOfBuildingWork?.constructionSwimmingPoolOrSpa ?? DEFAULT_NATURE_OF_BUILDING_WORK.constructionSwimmingPoolOrSpa,
            constructionSwimmingPoolOrSpaBarrier: data.natureOfBuildingWork?.constructionSwimmingPoolOrSpaBarrier ?? DEFAULT_NATURE_OF_BUILDING_WORK.constructionSwimmingPoolOrSpaBarrier,
            descriptionOfProposedBuildingWorks: data.natureOfBuildingWork?.descriptionOfProposedBuildingWorks ?? DEFAULT_NATURE_OF_BUILDING_WORK.descriptionOfProposedBuildingWorks,
            descriptionOfUseOfBuilding: data.natureOfBuildingWork?.descriptionOfUseOfBuilding ?? DEFAULT_NATURE_OF_BUILDING_WORK.descriptionOfUseOfBuilding,
        },
        buildingDetails: {
            proposedUseOfBuilding: data.buildingDetails?.proposedUseOfBuilding ?? DEFAULT_BUILDING_DETAILS.proposedUseOfBuilding,
            proposedClassOfBuilding: data.buildingDetails?.proposedClassOfBuilding ?? DEFAULT_BUILDING_DETAILS.proposedClassOfBuilding,
            currentUseOfBuilding: data.buildingDetails?.currentUseOfBuilding ?? DEFAULT_BUILDING_DETAILS.currentUseOfBuilding,
            currentClassOfBuilding: data.buildingDetails?.currentClassOfBuilding ?? DEFAULT_BUILDING_DETAILS.currentClassOfBuilding,
            includesSocialHousing: data.buildingDetails?.includesSocialHousing ?? DEFAULT_BUILDING_DETAILS.includesSocialHousing,
            isEmergencyRecoveryDwelling: data.buildingDetails?.isEmergencyRecoveryDwelling ?? DEFAULT_BUILDING_DETAILS.isEmergencyRecoveryDwelling,
        },
        costOfBuildingWork: {
            nonOwnerBuilder: {
                hasLumpSumContract: data.costOfBuildingWork?.nonOwnerBuilder?.hasLumpSumContract ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.hasLumpSumContract,
                lumpSumContractPrice: data.costOfBuildingWork?.nonOwnerBuilder?.lumpSumContractPrice ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.lumpSumContractPrice,
                hasCostPlusContract: data.costOfBuildingWork?.nonOwnerBuilder?.hasCostPlusContract ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.hasCostPlusContract,
                costPlusEstimatedTotal: data.costOfBuildingWork?.nonOwnerBuilder?.costPlusEstimatedTotal ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.costPlusEstimatedTotal,
                chattelsCost: data.costOfBuildingWork?.nonOwnerBuilder?.chattelsCost ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.chattelsCost,
                prescribedExcludedItemsCost: data.costOfBuildingWork?.nonOwnerBuilder?.prescribedExcludedItemsCost ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.prescribedExcludedItemsCost,
                relatesToMixedClasses: data.costOfBuildingWork?.nonOwnerBuilder?.relatesToMixedClasses ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.relatesToMixedClasses,
                relatesToMixedClassesNotes: data.costOfBuildingWork?.nonOwnerBuilder?.relatesToMixedClassesNotes ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.relatesToMixedClassesNotes,
                isStageOfWholeWork: data.costOfBuildingWork?.nonOwnerBuilder?.isStageOfWholeWork ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.isStageOfWholeWork,
                stageExtentDescription: data.costOfBuildingWork?.nonOwnerBuilder?.stageExtentDescription ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.stageExtentDescription,
                hasWholeLumpSumContract: data.costOfBuildingWork?.nonOwnerBuilder?.hasWholeLumpSumContract ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.hasWholeLumpSumContract,
                wholeLumpSumContractPrice: data.costOfBuildingWork?.nonOwnerBuilder?.wholeLumpSumContractPrice ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.wholeLumpSumContractPrice,
                hasWholeCostPlusContract: data.costOfBuildingWork?.nonOwnerBuilder?.hasWholeCostPlusContract ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.hasWholeCostPlusContract,
                wholeCostPlusEstimatedTotal: data.costOfBuildingWork?.nonOwnerBuilder?.wholeCostPlusEstimatedTotal ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.wholeCostPlusEstimatedTotal,
                wholeChattelsCost: data.costOfBuildingWork?.nonOwnerBuilder?.wholeChattelsCost ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.wholeChattelsCost,
                wholePrescribedExcludedItemsCost: data.costOfBuildingWork?.nonOwnerBuilder?.wholePrescribedExcludedItemsCost ?? DEFAULT_COST_OF_BUILDING_WORK.nonOwnerBuilder.wholePrescribedExcludedItemsCost,
            },
            ownerBuilder: {
                isOwnerBuilder: data.costOfBuildingWork?.ownerBuilder?.isOwnerBuilder ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.isOwnerBuilder,
                ownerBuilderCertificateOfConsentNo: data.costOfBuildingWork?.ownerBuilder?.ownerBuilderCertificateOfConsentNo ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerBuilderCertificateOfConsentNo,
                ownerBuilderEstimatedCost: data.costOfBuildingWork?.ownerBuilder?.ownerBuilderEstimatedCost ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerBuilderEstimatedCost,
                ownerEngagesBuilders: data.costOfBuildingWork?.ownerBuilder?.ownerEngagesBuilders ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerEngagesBuilders,
                engagedBuilders: (data.costOfBuildingWork?.ownerBuilder?.engagedBuilders ?? []).map((eb) => ({ amount: eb?.amount ?? "" })),
                ownerChattelsCost: data.costOfBuildingWork?.ownerBuilder?.ownerChattelsCost ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerChattelsCost,
                ownerPrescribedExcludedItemsCost: data.costOfBuildingWork?.ownerBuilder?.ownerPrescribedExcludedItemsCost ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerPrescribedExcludedItemsCost,
                ownerIsStageOfWholeWork: data.costOfBuildingWork?.ownerBuilder?.ownerIsStageOfWholeWork ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerIsStageOfWholeWork,
                ownerStageExtentDescription: data.costOfBuildingWork?.ownerBuilder?.ownerStageExtentDescription ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerStageExtentDescription,
                ownerWholeEstimatedCost: data.costOfBuildingWork?.ownerBuilder?.ownerWholeEstimatedCost ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerWholeEstimatedCost,
                ownerWholeChattelsCost: data.costOfBuildingWork?.ownerBuilder?.ownerWholeChattelsCost ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerWholeChattelsCost,
                ownerWholePrescribedExcludedItemsCost: data.costOfBuildingWork?.ownerBuilder?.ownerWholePrescribedExcludedItemsCost ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerWholePrescribedExcludedItemsCost,
                ownerHasLumpSumBuilderForStages: data.costOfBuildingWork?.ownerBuilder?.ownerHasLumpSumBuilderForStages ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerHasLumpSumBuilderForStages,
                ownerLumpSumStagesPrice: data.costOfBuildingWork?.ownerBuilder?.ownerLumpSumStagesPrice ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerLumpSumStagesPrice,
                ownerHasCostPlusBuilderForStages: data.costOfBuildingWork?.ownerBuilder?.ownerHasCostPlusBuilderForStages ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerHasCostPlusBuilderForStages,
                ownerCostPlusStagesEstimate: data.costOfBuildingWork?.ownerBuilder?.ownerCostPlusStagesEstimate ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerCostPlusStagesEstimate,
                ownerStagesChattelsCost: data.costOfBuildingWork?.ownerBuilder?.ownerStagesChattelsCost ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerStagesChattelsCost,
                ownerStagesExcludedItemsCost: data.costOfBuildingWork?.ownerBuilder?.ownerStagesExcludedItemsCost ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerStagesExcludedItemsCost,
                ownerRelatesToMixedClasses: data.costOfBuildingWork?.ownerBuilder?.ownerRelatesToMixedClasses ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerRelatesToMixedClasses,
                ownerRelatesToMixedClassesNotes: data.costOfBuildingWork?.ownerBuilder?.ownerRelatesToMixedClassesNotes ?? DEFAULT_COST_OF_BUILDING_WORK.ownerBuilder.ownerRelatesToMixedClassesNotes,
            },
        },
        signatureDeclaration: {
            applicantSignature: data.signatureDeclaration?.applicantSignature ?? DEFAULT_SIGNATURE_DECLARATION.applicantSignature,
            printName: data.signatureDeclaration?.printName ?? DEFAULT_SIGNATURE_DECLARATION.printName,
            signatureDate: data.signatureDeclaration?.signatureDate ?? DEFAULT_SIGNATURE_DECLARATION.signatureDate,
        },
    };
}
function modalToApiFormData(modal, recipient) {
    return {
        formType: "Building Permit Form",
        recipient,
        applicantRole: modal.applicantRole || undefined,
        applicantAcnArbn: modal.applicantAcnArbn,
        contactName: modal.contactName,
        email: modal.email,
        companyName: modal.companyName,
        phone: modal.phone,
        address: modal.address,
        servingAddress: modal.addressForServing,
        isLeaseeOrLicensee: modal.isLiaseeOrLicensee,
        lesseeResponsible: modal.lesseeResponsible,
        propertyDetails: {
            buildingName: modal.propertyDetails.buildingName,
            shopNo: modal.propertyDetails.shopNo,
            fullAddress: modal.propertyDetails.fullAddress,
            lotNumber: modal.propertyDetails.lotNumber,
            lpPs: modal.propertyDetails.lpPs,
            vol: modal.propertyDetails.vol,
            fol: modal.propertyDetails.fol,
            crownAllot: modal.propertyDetails.crownAllot,
            section: modal.propertyDetails.section,
            parish: modal.propertyDetails.parish,
            county: modal.propertyDetails.county,
            council: modal.propertyDetails.council,
            landOwnedByCrownOrPublicAuthority: modal.propertyDetails.landOwnedByCrownOrPublicAuthority,
        },
        buildingOwner: {
            name: modal.ownerName,
            contactPerson: modal.ownerContactPerson,
            email: modal.ownerEmail,
            company: modal.ownerCompanyName,
            phone: modal.ownerPhone,
            address: modal.ownerAddress,
            acnArbn: modal.acnArbn,
        },
        tenant: {
            contactPerson: modal.tenantContactPerson,
            email: modal.tenantEmail,
            company: modal.tenantCompanyName,
            phone: modal.tenantPhone,
            address: modal.tenantAddress,
        },
        builder: {
            name: modal.builderName,
            contactPerson: modal.builderContactPerson,
            email: modal.builderEmail,
            company: modal.builderCompanyName,
            phone: modal.builderPhone,
            address: modal.builderAddress,
            practitionerNo: modal.builderPractitionerNo,
            acnArbn: modal.builderAcnArbn,
            postalAddress: modal.builderPostalAddress,
            naturalPerson: {
                contactPerson: modal.builderNaturalPerson.contactPerson,
                email: modal.builderNaturalPerson.email,
                address: modal.builderNaturalPerson.address,
                phone: modal.builderNaturalPerson.phone,
            },
        },
        buildingPractitioners: modal.buildingPractitioners.map((bp) => ({
            name: bp.name,
            categoryOrClass: bp.categoryOrClass,
            registrationNo: bp.registrationNo,
        })),
        ownerBuilderSection: {
            intendsOwnerBuilder: modal.ownerBuilderSection.intendsOwnerBuilder,
            ownerBuilderCertificateOfConsentNo: modal.ownerBuilderSection.ownerBuilderCertificateOfConsentNo,
            signedByOwner: modal.ownerBuilderSection.signedByOwner,
        },
        natureOfBuildingWork: {
            constructionNewBuilding: modal.natureOfBuildingWork.constructionNewBuilding,
            demolitionBuilding: modal.natureOfBuildingWork.demolitionBuilding,
            reErectionBuilding: modal.natureOfBuildingWork.reErectionBuilding,
            alterationsExistingBuilding: modal.natureOfBuildingWork.alterationsExistingBuilding,
            removalBuilding: modal.natureOfBuildingWork.removalBuilding,
            extensionBuilding: modal.natureOfBuildingWork.extensionBuilding,
            tenancyFitout: modal.natureOfBuildingWork.tenancyFitout,
            changeOfUse: modal.natureOfBuildingWork.changeOfUse,
            otherDescriptionEnabled: modal.natureOfBuildingWork.otherDescriptionEnabled,
            otherDescription: modal.natureOfBuildingWork.otherDescription,
            constructionSwimmingPoolOrSpa: modal.natureOfBuildingWork.constructionSwimmingPoolOrSpa,
            constructionSwimmingPoolOrSpaBarrier: modal.natureOfBuildingWork.constructionSwimmingPoolOrSpaBarrier,
            descriptionOfProposedBuildingWorks: modal.natureOfBuildingWork.descriptionOfProposedBuildingWorks,
            descriptionOfUseOfBuilding: modal.natureOfBuildingWork.descriptionOfUseOfBuilding,
        },
        buildingDetails: {
            proposedUseOfBuilding: modal.buildingDetails.proposedUseOfBuilding,
            proposedClassOfBuilding: modal.buildingDetails.proposedClassOfBuilding,
            currentUseOfBuilding: modal.buildingDetails.currentUseOfBuilding,
            currentClassOfBuilding: modal.buildingDetails.currentClassOfBuilding,
            includesSocialHousing: modal.buildingDetails.includesSocialHousing,
            isEmergencyRecoveryDwelling: modal.buildingDetails.isEmergencyRecoveryDwelling,
        },
        costOfBuildingWork: {
            nonOwnerBuilder: {
                hasLumpSumContract: modal.costOfBuildingWork.nonOwnerBuilder.hasLumpSumContract,
                lumpSumContractPrice: modal.costOfBuildingWork.nonOwnerBuilder.lumpSumContractPrice,
                hasCostPlusContract: modal.costOfBuildingWork.nonOwnerBuilder.hasCostPlusContract,
                costPlusEstimatedTotal: modal.costOfBuildingWork.nonOwnerBuilder.costPlusEstimatedTotal,
                chattelsCost: modal.costOfBuildingWork.nonOwnerBuilder.chattelsCost,
                prescribedExcludedItemsCost: modal.costOfBuildingWork.nonOwnerBuilder.prescribedExcludedItemsCost,
                relatesToMixedClasses: modal.costOfBuildingWork.nonOwnerBuilder.relatesToMixedClasses,
                relatesToMixedClassesNotes: modal.costOfBuildingWork.nonOwnerBuilder.relatesToMixedClassesNotes,
                isStageOfWholeWork: modal.costOfBuildingWork.nonOwnerBuilder.isStageOfWholeWork,
                stageExtentDescription: modal.costOfBuildingWork.nonOwnerBuilder.stageExtentDescription,
                hasWholeLumpSumContract: modal.costOfBuildingWork.nonOwnerBuilder.hasWholeLumpSumContract,
                wholeLumpSumContractPrice: modal.costOfBuildingWork.nonOwnerBuilder.wholeLumpSumContractPrice,
                hasWholeCostPlusContract: modal.costOfBuildingWork.nonOwnerBuilder.hasWholeCostPlusContract,
                wholeCostPlusEstimatedTotal: modal.costOfBuildingWork.nonOwnerBuilder.wholeCostPlusEstimatedTotal,
                wholeChattelsCost: modal.costOfBuildingWork.nonOwnerBuilder.wholeChattelsCost,
                wholePrescribedExcludedItemsCost: modal.costOfBuildingWork.nonOwnerBuilder.wholePrescribedExcludedItemsCost,
            },
            ownerBuilder: {
                isOwnerBuilder: modal.costOfBuildingWork.ownerBuilder.isOwnerBuilder,
                ownerBuilderCertificateOfConsentNo: modal.costOfBuildingWork.ownerBuilder.ownerBuilderCertificateOfConsentNo,
                ownerBuilderEstimatedCost: modal.costOfBuildingWork.ownerBuilder.ownerBuilderEstimatedCost,
                ownerEngagesBuilders: modal.costOfBuildingWork.ownerBuilder.ownerEngagesBuilders,
                engagedBuilders: modal.costOfBuildingWork.ownerBuilder.engagedBuilders.map((eb) => ({ amount: eb.amount })),
                ownerChattelsCost: modal.costOfBuildingWork.ownerBuilder.ownerChattelsCost,
                ownerPrescribedExcludedItemsCost: modal.costOfBuildingWork.ownerBuilder.ownerPrescribedExcludedItemsCost,
                ownerIsStageOfWholeWork: modal.costOfBuildingWork.ownerBuilder.ownerIsStageOfWholeWork,
                ownerStageExtentDescription: modal.costOfBuildingWork.ownerBuilder.ownerStageExtentDescription,
                ownerWholeEstimatedCost: modal.costOfBuildingWork.ownerBuilder.ownerWholeEstimatedCost,
                ownerWholeChattelsCost: modal.costOfBuildingWork.ownerBuilder.ownerWholeChattelsCost,
                ownerWholePrescribedExcludedItemsCost: modal.costOfBuildingWork.ownerBuilder.ownerWholePrescribedExcludedItemsCost,
                ownerHasLumpSumBuilderForStages: modal.costOfBuildingWork.ownerBuilder.ownerHasLumpSumBuilderForStages,
                ownerLumpSumStagesPrice: modal.costOfBuildingWork.ownerBuilder.ownerLumpSumStagesPrice,
                ownerHasCostPlusBuilderForStages: modal.costOfBuildingWork.ownerBuilder.ownerHasCostPlusBuilderForStages,
                ownerCostPlusStagesEstimate: modal.costOfBuildingWork.ownerBuilder.ownerCostPlusStagesEstimate,
                ownerStagesChattelsCost: modal.costOfBuildingWork.ownerBuilder.ownerStagesChattelsCost,
                ownerStagesExcludedItemsCost: modal.costOfBuildingWork.ownerBuilder.ownerStagesExcludedItemsCost,
                ownerRelatesToMixedClasses: modal.costOfBuildingWork.ownerBuilder.ownerRelatesToMixedClasses,
                ownerRelatesToMixedClassesNotes: modal.costOfBuildingWork.ownerBuilder.ownerRelatesToMixedClassesNotes,
            },
        },
        signatureDeclaration: {
            applicantSignature: modal.signatureDeclaration.applicantSignature,
            printName: modal.signatureDeclaration.printName,
            signatureDate: modal.signatureDeclaration.signatureDate,
        },
    };
}
export const BuildingPermitModal = ({ isOpen, onClose, selectedSurveyor, buildingSurveyorId, formId, jobId, job, prefillFromNotes = false, onDraftSaved, onSubmitSuccess, }) => {
    const { success, error: showError } = useToast();
    const [saving, setSaving] = useState(false);
    const [modalFormData, setModalFormData] = useState(DEFAULT_FORM_DATA);
    const [formTitle, setFormTitle] = useState(DEFAULT_FORM_TITLE);
    const [regulationText, setRegulationText] = useState(DEFAULT_REGULATION_TEXT);
    const [formLoading, setFormLoading] = useState(false);
    const [formLoadError, setFormLoadError] = useState(null);
    const [customLogo, setCustomLogo] = useState("/logo.png");
    const [customCompanyName, setCustomCompanyName] = useState("group building surveyors");
    const [fieldLabels, setFieldLabels] = useState({});
    const [deletedFields, setDeletedFields] = useState(new Set());
    const apiFormData = modalToApiFormData(modalFormData, selectedSurveyor);
    useEffect(() => {
        if (!isOpen)
            return;
        const resetFormBranding = () => {
            setCustomLogo("/logo.png");
            setCustomCompanyName("group building surveyors");
            setFieldLabels({});
            setDeletedFields(new Set());
        };
        const loadFormBranding = async () => {
            if (!formId) {
                resetFormBranding();
                return;
            }
            try {
                const res = await formsService.getForm(formId);
                const form = res.data;
                setFormTitle(form.name || form.templateName || DEFAULT_FORM_TITLE);
                setRegulationText(form.templateName || DEFAULT_REGULATION_TEXT);
                const formData = form.data;
                if (formData) {
                    setCustomLogo(formData.customLogo || "/logo.png");
                    setCustomCompanyName(formData.customCompanyName || "group building surveyors");
                    setFieldLabels(formData.fieldLabels || {});
                    const deletedArr = formData.deletedFields || [];
                    setDeletedFields(new Set(deletedArr));
                }
                else {
                    resetFormBranding();
                }
                return form;
            }
            catch {
                resetFormBranding();
                return null;
            }
        };
        if (prefillFromNotes && job?.notes) {
            const draftData = parseBuildingPermitDraftFromNotes(job.notes);
            if (draftData) {
                setModalFormData(draftData);
                setFormLoadError(null);
                setFormLoading(false);
                void loadFormBranding();
                return;
            }
        }
        if (formId) {
            setFormLoading(true);
            setFormLoadError(null);
            formsService
                .getForm(formId)
                .then((res) => {
                const form = res.data;
                const apiData = form.data;
                setFormTitle(apiData?.customFormTitle ||
                    form.name ||
                    form.templateName ||
                    DEFAULT_FORM_TITLE);
                setRegulationText(buildRegulationText(apiData?.customSubtitleDate));
                setModalFormData(mapApiFormDataToModal(form.data));
                const formData = form.data;
                if (formData) {
                    setCustomLogo(formData.customLogo || "/logo.png");
                    setCustomCompanyName(formData.customCompanyName || "group building surveyors");
                    setFieldLabels(formData.fieldLabels || {});
                    const deletedArr = formData.deletedFields || [];
                    setDeletedFields(new Set(deletedArr));
                }
                else {
                    resetFormBranding();
                }
            })
                .catch((err) => {
                console.error("Failed to load form:", err);
                setFormLoadError("Failed to load form. Please try again.");
                setFormTitle(DEFAULT_FORM_TITLE);
                setRegulationText(DEFAULT_REGULATION_TEXT);
                setModalFormData(DEFAULT_FORM_DATA);
                resetFormBranding();
            })
                .finally(() => {
                setFormLoading(false);
            });
        }
        else {
            setFormTitle(DEFAULT_FORM_TITLE);
            setRegulationText(DEFAULT_REGULATION_TEXT);
            setModalFormData(DEFAULT_FORM_DATA);
            setFormLoadError(null);
            resetFormBranding();
        }
    }, [isOpen, formId, prefillFromNotes, job?.notes]);
    const handleFieldChange = useCallback((field, value) => {
        setModalFormData((prev) => {
            const next = { ...prev };
            switch (field) {
                case "recipient":
                    break;
                case "applicantRole":
                    next.applicantRole = value ?? "";
                    break;
                case "applicantAcnArbn":
                    next.applicantAcnArbn = String(value ?? "");
                    break;
                case "contactName":
                    next.contactName = String(value ?? "");
                    break;
                case "email":
                    next.email = String(value ?? "");
                    break;
                case "companyName":
                    next.companyName = String(value ?? "");
                    break;
                case "phone":
                    next.phone = String(value ?? "");
                    break;
                case "address":
                    next.address = String(value ?? "");
                    break;
                case "servingAddress":
                    next.addressForServing = String(value ?? "");
                    break;
                case "isLeaseeOrLicensee":
                    next.isLiaseeOrLicensee = Boolean(value);
                    break;
                case "lesseeResponsible":
                    next.lesseeResponsible = Boolean(value);
                    break;
            }
            return next;
        });
    }, []);
    const handleBuildingOwnerChange = useCallback((field, value) => {
        setModalFormData((prev) => {
            const next = { ...prev };
            switch (field) {
                case "name":
                    next.ownerName = value;
                    break;
                case "contactPerson":
                    next.ownerContactPerson = value;
                    break;
                case "email":
                    next.ownerEmail = value;
                    break;
                case "company":
                    next.ownerCompanyName = value;
                    break;
                case "phone":
                    next.ownerPhone = value;
                    break;
                case "address":
                    next.ownerAddress = value;
                    break;
                case "acnArbn":
                case "acn":
                    next.acnArbn = value;
                    break;
            }
            return next;
        });
    }, []);
    const handleBuilderChange = useCallback((field, value) => {
        setModalFormData((prev) => {
            const next = { ...prev };
            switch (field) {
                case "name":
                    next.builderName = value;
                    break;
                case "contactPerson":
                    next.builderContactPerson = value;
                    break;
                case "email":
                    next.builderEmail = value;
                    break;
                case "company":
                    next.builderCompanyName = value;
                    break;
                case "phone":
                    next.builderPhone = value;
                    break;
                case "address":
                    next.builderAddress = value;
                    break;
                case "practitionerNo":
                    next.builderPractitionerNo = value;
                    break;
                case "acnArbn":
                    next.builderAcnArbn = value;
                    break;
                case "postalAddress":
                    next.builderPostalAddress = value;
                    break;
            }
            return next;
        });
    }, []);
    const handleTenantChange = useCallback((field, value) => {
        setModalFormData((prev) => {
            const next = { ...prev };
            switch (field) {
                case "contactPerson":
                    next.tenantContactPerson = value;
                    break;
                case "email":
                    next.tenantEmail = value;
                    break;
                case "company":
                    next.tenantCompanyName = value;
                    break;
                case "phone":
                    next.tenantPhone = value;
                    break;
                case "address":
                    next.tenantAddress = value;
                    break;
            }
            return next;
        });
    }, []);
    const handlePropertyDetailsChange = useCallback((field, value) => {
        setModalFormData((prev) => {
            const next = { ...prev, propertyDetails: { ...prev.propertyDetails } };
            switch (field) {
                case "buildingName":
                    next.propertyDetails.buildingName = String(value);
                    break;
                case "shopNo":
                    next.propertyDetails.shopNo = String(value);
                    break;
                case "fullAddress":
                    next.propertyDetails.fullAddress = String(value);
                    break;
                case "lotNumber":
                    next.propertyDetails.lotNumber = String(value);
                    break;
                case "lpPs":
                    next.propertyDetails.lpPs = String(value);
                    break;
                case "vol":
                    next.propertyDetails.vol = String(value);
                    break;
                case "fol":
                    next.propertyDetails.fol = String(value);
                    break;
                case "crownAllot":
                    next.propertyDetails.crownAllot = String(value);
                    break;
                case "section":
                    next.propertyDetails.section = String(value);
                    break;
                case "parish":
                    next.propertyDetails.parish = String(value);
                    break;
                case "county":
                    next.propertyDetails.county = String(value);
                    break;
                case "council":
                    next.propertyDetails.council = String(value);
                    break;
                case "landOwnedByCrownOrPublicAuthority":
                    next.propertyDetails.landOwnedByCrownOrPublicAuthority = Boolean(value);
                    break;
            }
            return next;
        });
    }, []);
    const handleBuilderNaturalPersonChange = useCallback((field, value) => {
        setModalFormData((prev) => {
            const next = { ...prev, builderNaturalPerson: { ...prev.builderNaturalPerson } };
            switch (field) {
                case "contactPerson":
                    next.builderNaturalPerson.contactPerson = value;
                    break;
                case "email":
                    next.builderNaturalPerson.email = value;
                    break;
                case "address":
                    next.builderNaturalPerson.address = value;
                    break;
                case "phone":
                    next.builderNaturalPerson.phone = value;
                    break;
            }
            return next;
        });
    }, []);
    const handleBuildingPractitionerChange = useCallback((index, field, value) => {
        setModalFormData((prev) => {
            const list = [...prev.buildingPractitioners];
            if (list[index]) {
                list[index] = { ...list[index], [field]: value };
            }
            return { ...prev, buildingPractitioners: list };
        });
    }, []);
    const handleAddBuildingPractitioner = useCallback(() => {
        setModalFormData((prev) => ({
            ...prev,
            buildingPractitioners: [
                ...prev.buildingPractitioners,
                { ...DEFAULT_BUILDING_PRACTITIONER },
            ],
        }));
    }, []);
    const handleRemoveBuildingPractitioner = useCallback((index) => {
        setModalFormData((prev) => {
            const list = [...prev.buildingPractitioners];
            list.splice(index, 1);
            return {
                ...prev,
                buildingPractitioners: list.length > 0 ? list : [{ ...DEFAULT_BUILDING_PRACTITIONER }],
            };
        });
    }, []);
    const handleOwnerBuilderSectionChange = useCallback((field, value) => {
        setModalFormData((prev) => ({
            ...prev,
            ownerBuilderSection: {
                ...prev.ownerBuilderSection,
                [field]: value,
            },
        }));
    }, []);
    const handleNatureOfBuildingWorkChange = useCallback((field, value) => {
        setModalFormData((prev) => ({
            ...prev,
            natureOfBuildingWork: {
                ...prev.natureOfBuildingWork,
                [field]: value,
            },
        }));
    }, []);
    const handleBuildingDetailsChange = useCallback((field, value) => {
        setModalFormData((prev) => ({
            ...prev,
            buildingDetails: {
                ...prev.buildingDetails,
                [field]: value,
            },
        }));
    }, []);
    const handleCostOfBuildingWorkChange = useCallback((section, field, value) => {
        setModalFormData((prev) => ({
            ...prev,
            costOfBuildingWork: {
                ...prev.costOfBuildingWork,
                [section]: {
                    ...prev.costOfBuildingWork[section],
                    [field]: value,
                },
            },
        }));
    }, []);
    const handleEngagedBuilderChange = useCallback((index, value) => {
        setModalFormData((prev) => {
            const builders = [...(prev.costOfBuildingWork.ownerBuilder.engagedBuilders || [])];
            if (index < builders.length) {
                builders[index] = { amount: value };
            }
            return {
                ...prev,
                costOfBuildingWork: {
                    ...prev.costOfBuildingWork,
                    ownerBuilder: {
                        ...prev.costOfBuildingWork.ownerBuilder,
                        engagedBuilders: builders,
                    },
                },
            };
        });
    }, []);
    const handleAddEngagedBuilder = useCallback(() => {
        setModalFormData((prev) => ({
            ...prev,
            costOfBuildingWork: {
                ...prev.costOfBuildingWork,
                ownerBuilder: {
                    ...prev.costOfBuildingWork.ownerBuilder,
                    engagedBuilders: [
                        ...(prev.costOfBuildingWork.ownerBuilder.engagedBuilders || []),
                        { amount: "" },
                    ],
                },
            },
        }));
    }, []);
    const handleSignatureDeclarationChange = useCallback((field, value) => {
        setModalFormData((prev) => ({
            ...prev,
            signatureDeclaration: {
                ...prev.signatureDeclaration,
                [field]: value,
            },
        }));
    }, []);
    if (!isOpen)
        return null;
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    const handleSaveDraft = async () => {
        if (!jobId) {
            console.error("No job ID provided");
            return;
        }
        setSaving(true);
        try {
            const existingAdditionalInfo = getAdditionalInformationFromNotes(job?.notes);
            const additionalInformation = modalFormData.additionalInformation?.trim() ||
                existingAdditionalInfo ||
                "";
            const buildingPermitData = JSON.stringify({
                ...modalFormData,
                additionalInformation: additionalInformation || undefined,
                selectedSurveyor,
                status: "draft",
                lastUpdated: new Date().toISOString(),
            });
            const draftNotes = `${DRAFT_NOTES_PREFIX}${buildingPermitData}`;
            const jobToUpdate = job ?? (await jobsService.getJob(jobId)).data;
            const dto = jobToUpdateDto(jobToUpdate, { notes: draftNotes });
            await jobsService.updateJob(jobId, dto);
            success("Draft saved successfully!");
            onDraftSaved?.();
        }
        catch (err) {
            console.error("Failed to save draft:", err);
            showError("Failed to save draft. Please try again.");
        }
        finally {
            setSaving(false);
        }
    };
    const handleSubmit = async () => {
        if (!jobId) {
            console.error("No job ID provided");
            return;
        }
        setSaving(true);
        try {
            const existingAdditionalInfo = getAdditionalInformationFromNotes(job?.notes);
            const additionalInformation = modalFormData.additionalInformation?.trim() ||
                existingAdditionalInfo ||
                "";
            const buildingPermitData = JSON.stringify({
                ...modalFormData,
                additionalInformation: additionalInformation || undefined,
                selectedSurveyor,
                status: "submitted",
                submittedAt: new Date().toISOString(),
            });
            const submitNotes = `Building Permit Application (Submitted): ${buildingPermitData}`;
            const submitOverrides = {
                buildingPermitRequested: true,
                buildingPermitRequestedDate: nowAUSISO(),
                buildingSurveyorId: buildingSurveyorId || undefined,
                surveyorStatus: "PENDING",
                notes: submitNotes,
            };
            const jobToUpdate = job ?? (await jobsService.getJob(jobId)).data;
            const dto = jobToUpdateDto(jobToUpdate, submitOverrides);
            await jobsService.updateJob(jobId, dto);
            onSubmitSuccess?.();
            onClose();
        }
        catch (err) {
            console.error("Failed to submit building permit:", err);
            showError("Failed to submit building permit. Please try again.");
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsx("div", { className: styles.backdrop, onClick: handleBackdropClick, children: _jsxs("div", { className: styles.modal, children: [_jsx("div", { className: styles.header, children: _jsx("button", { className: styles.closeBtn, onClick: onClose, type: "button", children: _jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) }) }), _jsx("div", { className: styles.content, children: formLoading ? (_jsx("div", { className: styles.formLoading, children: "Loading form..." })) : formLoadError ? (_jsx("div", { className: styles.formLoadError, children: formLoadError })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.formHeader, children: [_jsxs("div", { className: styles.logoSection, children: [_jsx("div", { className: styles.logo, children: _jsx("img", { src: customLogo, alt: "Company Logo", className: styles.logoImage }) }), _jsx("h1", { children: customCompanyName })] }), _jsxs("div", { className: styles.formTitle, children: [_jsx("p", { className: styles.regulationText, children: regulationText }), _jsx("h2", { className: styles.mainTitle, children: formTitle })] })] }), _jsx(FormFieldsEditor, { formData: apiFormData, onFieldChange: handleFieldChange, onBuildingOwnerChange: handleBuildingOwnerChange, onBuilderChange: handleBuilderChange, onPropertyDetailsChange: handlePropertyDetailsChange, onTenantChange: handleTenantChange, onBuilderNaturalPersonChange: handleBuilderNaturalPersonChange, onBuildingPractitionerChange: handleBuildingPractitionerChange, onAddBuildingPractitioner: handleAddBuildingPractitioner, onRemoveBuildingPractitioner: handleRemoveBuildingPractitioner, onOwnerBuilderSectionChange: handleOwnerBuilderSectionChange, onNatureOfBuildingWorkChange: handleNatureOfBuildingWorkChange, onBuildingDetailsChange: handleBuildingDetailsChange, onCostOfBuildingWorkChange: handleCostOfBuildingWorkChange, onEngagedBuilderChange: handleEngagedBuilderChange, onAddEngagedBuilder: handleAddEngagedBuilder, onSignatureDeclarationChange: handleSignatureDeclarationChange, editingLabels: fieldLabels, deletedFields: deletedFields, showLabelEditing: false, showFieldDeletion: false, showLogo: false }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { className: styles.saveDraftBtn, onClick: handleSaveDraft, type: "button", disabled: saving, children: saving ? "SAVING..." : "SAVE DRAFT" }), _jsx("button", { className: styles.submitBtn, onClick: handleSubmit, type: "button", disabled: saving, children: saving ? "SUBMITTING..." : "SUBMIT" })] })] })) })] }) }));
};
